import * as React from 'react';
import * as Headless from '@headlessui/react';
import { useRouteError } from 'react-router-dom';

import { CheckIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import { TransitionDiv } from './async';

const ErrorDetails = (props: { error: Error | { data: string; error?: Error } }) => {
  const [copied, setCopied] = React.useState(false);

  const errorMessage = 'data' in props.error ? props.error.data : props.error.message;
  const errorStack =
    props.error instanceof Error
      ? props.error.stack
      : 'data' in props.error
        ? props.error.error?.stack
        : '';

  React.useEffect(() => {
    if (!copied) {
      return;
    }

    navigator.clipboard.writeText((errorMessage + '\n\n' + errorStack).trim());
    setTimeout(() => setCopied(false), 3000);
  }, [copied]);

  return (
    <div className="relative rounded-xl p-5 bg-neutral-200 dark:bg-neutral-700 flex flex-col gap-8 break-words">
      <Headless.Button
        className="absolute top-0 right-0 p-2 bg-neutral-300 dark:bg-neutral-600 rounded-tr-xl rounded-bl-xl"
        onClick={() => setCopied(true)}
      >
        {copied ? (
          <CheckIcon className="size-4 text-neutral-700 dark:text-neutral-200" />
        ) : (
          <ClipboardIcon className="size-4 text-neutral-700 dark:text-neutral-200" />
        )}
      </Headless.Button>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <div className="text-xs uppercase text-neutral-500 dark:text-neutral-400 font-semibold pb-1">
            Error
          </div>
          <div className="text-xs font-mono whitespace-pre-wrap text-neutral-700 dark:text-neutral-200">
            {errorMessage}
          </div>
        </div>
        {!!errorStack && (
          <div className="flex flex-col gap-1">
            <div className="text-xs uppercase text-neutral-500 dark:text-neutral-400 font-semibold pb-1">
              Stack Trace
            </div>
            <div className="text-xs font-mono whitespace-pre-wrap text-neutral-700 dark:text-neutral-200">
              {errorStack}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ErrorBoundary = () => {
  const error = useRouteError() as { data: string; error: Error };

  return (
    <TransitionDiv className="m-auto w-full max-w-screen-md pt-24">
      <h1 className="text-base/4 font-semibold text-primary-500 dark:text-primary-400">
        Application error
      </h1>
      <h2 className="text-3xl/8 dark:text-white">Something went wrong</h2>
      <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-400">
        There was an error while trying to perform your requested action. If the issue persists,
        please contact our team via the chat widget or via email at{' '}
        <a href="mailto:support@getcara.ai">support@getcara.ai</a> and include this error for
        reference.
      </p>

      <div className="my-8 grid grid-cols-3 gap-4">
        <div className="col-span-3">
          <ErrorDetails error={error} />
        </div>
      </div>
    </TransitionDiv>
  );
};
